import { useState } from 'react';

interface LeaderBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  leaderName: string;
  leaderTitle: string;
}

export default function LeaderBookingModal({ isOpen, onClose, leaderName, leaderTitle }: LeaderBookingModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    title: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    topics: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        ...formData,
        leaderName,
        leaderTitle
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d65f4993fhhvgn1ui75g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
          setFormData({
            name: '',
            email: '',
            company: '',
            title: '',
            phone: '',
            preferredDate: '',
            preferredTime: '',
            topics: ''
          });
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-6 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-2xl font-bold mb-2">Book 1:1 Meeting</h3>
              <p className="text-teal-50 text-sm">30-Minute Executive Session with {leaderName}</p>
              <p className="text-teal-100 text-xs mt-1">{leaderTitle}</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-teal-100 transition-colors">
              <i className="ri-close-line text-3xl"></i>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5" data-readdy-form id="leader-booking-form">
          <input type="hidden" name="leaderName" value={leaderName} />
          <input type="hidden" name="leaderTitle" value={leaderTitle} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                placeholder="john.smith@company.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Company *</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                placeholder="Company Name"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Job Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                placeholder="CIO, CTO, CDO, etc."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
              placeholder="+971 XX XXX XXXX"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Date *</label>
              <input
                type="date"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
                required
                min="2026-02-11"
                max="2026-02-12"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Time *</label>
              <select
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
              >
                <option value="">Select time slot</option>
                <option value="09:00-09:30">09:00 - 09:30</option>
                <option value="09:30-10:00">09:30 - 10:00</option>
                <option value="10:00-10:30">10:00 - 10:30</option>
                <option value="10:30-11:00">10:30 - 11:00</option>
                <option value="11:00-11:30">11:00 - 11:30</option>
                <option value="11:30-12:00">11:30 - 12:00</option>
                <option value="12:00-12:30">12:00 - 12:30</option>
                <option value="14:00-14:30">14:00 - 14:30</option>
                <option value="14:30-15:00">14:30 - 15:00</option>
                <option value="15:00-15:30">15:00 - 15:30</option>
                <option value="15:30-16:00">15:30 - 16:00</option>
                <option value="16:00-16:30">16:00 - 16:30</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Discussion Topics</label>
            <textarea
              name="topics"
              value={formData.topics}
              onChange={handleChange}
              maxLength={500}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm resize-none"
              placeholder="What would you like to discuss? (AI scaling, governance frameworks, ROI measurement, etc.)"
            ></textarea>
            <p className="text-xs text-gray-500 mt-1">{formData.topics.length}/500 characters</p>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <i className="ri-map-pin-line text-teal-600 text-xl mt-0.5"></i>
              <div>
                <p className="text-sm font-semibold text-gray-800">Meeting Location</p>
                <p className="text-sm text-gray-600">Booth TFG 28 · Atlantis, The Palm · Dubai</p>
              </div>
            </div>
          </div>

          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
              <p className="text-sm text-green-800 font-medium">Meeting request submitted successfully! We'll confirm your slot shortly.</p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <i className="ri-error-warning-fill text-red-600 text-xl"></i>
              <p className="text-sm text-red-800 font-medium">Something went wrong. Please try again.</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-4 rounded-lg font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? 'Submitting...' : 'Confirm Meeting Request'}
          </button>
        </form>
      </div>
    </div>
  );
}
